import React from "react";

export interface EmptyStateProps {
  variant?: "camera" | "chart" | "people";
  title: string;
  description?: string;
  isDarkMode?: boolean;
  className?: string;
}

const ICONS = {
  camera: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 13v7a2 2 0 01-2 2H7a2 2 0 01-2-2v-7" />
    </svg>
  ),
  chart: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  people: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  variant = "camera",
  title,
  description,
  isDarkMode = false,
  className = "",
}) => {
  const iconColor = isDarkMode ? "text-teal-400/60" : "text-teal-500/50";
  const titleColor = isDarkMode ? "text-slate-200" : "text-slate-700";
  const descColor = isDarkMode ? "text-slate-400" : "text-slate-500";

  return (
    <div
      className={`
        flex flex-col items-center justify-center py-10 px-4 sm:py-16 sm:px-8 rounded-2xl
        ${isDarkMode
          ? "bg-gradient-to-b from-slate-800/50 to-slate-800/30 border border-slate-700/40"
          : "bg-gradient-to-b from-slate-50 to-white border border-slate-200/60"}
        ${className}
      `}
    >
      <div
        className={`
          mb-4 sm:mb-5 p-3.5 sm:p-5 rounded-2xl ring-1
          ${isDarkMode ? "bg-slate-700/20 ring-slate-600/30" : "bg-white/80 ring-slate-200/50"}
          ${iconColor}
        `}
      >
        {ICONS[variant]}
      </div>
      <h3 className={`text-base sm:text-lg font-semibold ${titleColor} text-center mb-2`}>
        {title}
      </h3>
      {description && (
        <p className={`text-xs sm:text-sm ${descColor} text-center max-w-sm leading-relaxed`}>
          {description}
        </p>
      )}
    </div>
  );
};

export default EmptyState;
